import { useCallback, useEffect, useState } from 'react'
import Icon from '../../components/Icon'
import type { ApiError } from '../../lib/api'
import type { VerificationKind, VerificationMethod } from '../../../shared/market/verification'
import * as api from './api'
import VerifiedBadge from './VerifiedBadge'
import { vt, vtCode, type VerificationKey } from './strings'
import './verification.css'

type Load = { state: 'loading' } | { state: 'off' } | { state: 'error' } | { state: 'ready'; status: api.VerificationStatusResponse; policy: api.PolicyResponse }

/**
 * Optional Vieques residency verification, resident side. Flag-gated: when the server answers 404 the page
 * says it is not available and nothing else loads. Reached at #/verify; not linked from the navigation yet.
 */
export default function VerificationPage() {
  const [load, setLoad] = useState<Load>({ state: 'loading' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [method, setMethod] = useState<VerificationMethod>('id_address')
  const [note, setNote] = useState('')

  const refresh = useCallback(async () => {
    try {
      const [status, policy] = await Promise.all([api.getStatus(), api.getPolicy()])
      setLoad({ state: 'ready', status, policy })
    } catch (e) {
      setLoad((e as ApiError).status === 404 ? { state: 'off' } : { state: 'error' })
    }
  }, [])
  useEffect(() => { void refresh() }, [refresh])

  async function act(run: () => Promise<unknown>) {
    if (busy) return
    setBusy(true)
    setError('')
    try { await run(); await refresh() } catch (e) { setError((e as ApiError).message || vt('error')) } finally { setBusy(false) }
  }

  if (load.state === 'loading') return <p role="status" className="market-empty">{vt('loading')}</p>
  if (load.state === 'off') return <section className="v-page"><h1>{vt('unavailableTitle')}</h1><p>{vt('unavailableText')}</p><a className="market-back" href="#/"><Icon name="back" />{vt('back')}</a></section>
  if (load.state === 'error') return <section className="v-page"><p role="alert" className="error">{vt('error')}</p></section>

  const { status, policy } = load
  const synthetic = Boolean(status.provider?.synthetic)

  return (
    <section className="v-page">
      <div className="v-head">
        <span className="item-state">{vt('optional')}</span>
        <h1>{vt('title')}</h1>
        <p>{vt('lede')}</p>
        <p className="v-note"><Icon name="info" size={18} />{vt('notAGuarantee')}</p>
        {synthetic && <p className="v-test" role="status"><Icon name="alert" size={18} />{vt('testMode')}</p>}
      </div>
      {error && <p className="error" role="alert">{error}</p>}

      {!status.consent.given ? (
        <article className="v-card" aria-labelledby="v-consent">
          <h2 id="v-consent">{vt('consentTitle')}</h2>
          <dl className="v-facts">
            <dt>{vt('willCheck')}</dt><dd>{vt('checkIdentity')}<br />{vt('checkResidency')}</dd>
            <dt>{vt('provider')}</dt><dd>{vt('providerText')}{policy.provider ? ` (${policy.provider.name})` : ''}</dd>
            <dt>{vt('weKeep')}</dt><dd>{vt('weKeepText')}</dd>
            <dt>{vt('publicTitle')}</dt><dd>{vt('publicText')}</dd>
            <dt>{vt('deletionTitle')}</dt><dd>{vt('deletionText')}</dd>
          </dl>
          <label className="v-agree"><input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />{vt('agree')}</label>
          <button className="market-button coral" disabled={!agreed || busy} onClick={() => void act(() => api.giveConsent(policy.consentVersion))}>{vt('agreeAndContinue')}<Icon name="arrow" /></button>
        </article>
      ) : (
        <div className="v-checks">
          {(['identity', 'residency'] as const).map((kind) => (
            <CheckCard key={kind} kind={kind} check={status.checks[kind]} busy={busy} synthetic={synthetic}
              method={method} setMethod={setMethod} identityDone={status.checks.identity?.status === 'verified'}
              onStart={(m) => act(() => api.startCheck(kind, m))}
              onWithdraw={(id) => act(() => api.withdrawCheck(id))}
              onRespond={(id) => act(() => api.respondToCheck(id))}
              onSimulate={(id, type, extra) => act(() => api.simulate(id, type, extra))} />
          ))}
          <article className="v-card" aria-labelledby="v-help">
            <h2 id="v-help">{vt('helpTitle')}</h2>
            <p>{vt('helpText')}</p>
            {(['identity', 'residency'] as const).map((kind) => {
              const c = status.checks[kind]
              if (!c || !c.canAppeal && !c.appealOpen) return null
              return c.appealOpen
                ? <p key={kind} className="v-note"><Icon name="clock" size={18} />{vt('appealOpen')}</p>
                : <div key={kind}><label>{vt('appealNote')}<textarea maxLength={500} rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></label><button className="market-button secondary" disabled={busy} onClick={() => void act(() => api.appealCheck(c.id, note))}>{vt('appeal')}</button></div>
            })}
            <button className="market-link danger" disabled={busy} onClick={() => { if (confirm(vt('withdrawAllConfirm'))) void act(() => api.withdrawConsent()) }}>{vt('withdrawAll')}</button>
          </article>
        </div>
      )}
    </section>
  )
}

function CheckCard(p: {
  kind: VerificationKind; check: api.CheckSummary | null; busy: boolean; synthetic: boolean
  method: VerificationMethod; setMethod: (m: VerificationMethod) => void; identityDone: boolean
  onStart: (m?: VerificationMethod) => void; onWithdraw: (id: string) => void; onRespond: (id: string) => void
  onSimulate: (id: string, type: string, extra?: Record<string, unknown>) => void
}) {
  const { kind, check } = p
  const title = vt(kind === 'identity' ? 'identityTitle' : 'residencyTitle')
  const text = vt(kind === 'identity' ? 'identityText' : 'residencyText')
  const status = check?.status
  const canStart = !check || check.canStart
  return (
    <article className="v-card" aria-labelledby={`v-${kind}`} data-status={status ?? 'none'}>
      <div className="v-card-head">
        <h2 id={`v-${kind}`}>{title}</h2>
        <span className="v-chip" data-status={status ?? 'none'}>{status ? vt(status as VerificationKey) : vt('notStarted')}</span>
      </div>
      <p>{text}</p>
      {status === 'verified' && check?.checkedOn && <p className="v-badge-row"><VerifiedBadge kind={kind} badge={{ checkedOn: check.checkedOn, validUntil: check.validUntil }} /></p>}
      {status === 'pending' && <p className="v-note">{vt('pendingHelp')}</p>}
      {status === 'needs_info' && <p className="v-note">{vtCode(check?.guidance) ?? vt('needsInfoHelp')}</p>}
      {status === 'unsuccessful' && <p className="v-note">{vt('unsuccessfulHelp')}</p>}
      {status === 'expired' && <p className="v-note">{vt('expiredHelp')}</p>}
      {status === 'withdrawn' && <p className="v-note">{vt('withdrawnHelp')}</p>}

      {kind === 'residency' && canStart && (
        <fieldset className="v-methods">
          <legend>{vt('methodTitle')}</legend>
          <label className={p.method === 'id_address' ? 'on' : ''}><input type="radio" name="residency-method" checked={p.method === 'id_address'} onChange={() => p.setMethod('id_address')} /><strong>{vt('methodId')}</strong><small>{vt('methodIdHint')}</small></label>
          <label className={p.method === 'manual_evidence' ? 'on' : ''}><input type="radio" name="residency-method" checked={p.method === 'manual_evidence'} onChange={() => p.setMethod('manual_evidence')} /><strong>{vt('methodManual')}</strong><small>{vt('methodManualHint')}</small></label>
        </fieldset>
      )}
      <div className="v-actions">
        {canStart && <button className="market-button coral" disabled={p.busy || kind === 'residency' && p.method === 'id_address' && !p.identityDone} onClick={() => p.onStart(kind === 'residency' ? p.method : undefined)}>{status ? vt('retry') : vt('start')}<Icon name="arrow" /></button>}
        {check?.canRespond && <button className="market-button secondary" disabled={p.busy} onClick={() => p.onRespond(check.id)}>{vt('respond')}</button>}
        {check?.canWithdraw && <button className="market-link" disabled={p.busy} onClick={() => p.onWithdraw(check.id)}>{vt('withdraw')}</button>}
      </div>
      {p.synthetic && check && status === 'pending' && (
        <div className="v-test-controls">
          <strong>{vt('testChecks')}</strong>
          <button className="market-button secondary" disabled={p.busy} onClick={() => p.onSimulate(check.id, 'completed', { addressRegion: 'vieques' })}>{vt('testPass')}</button>
          {kind === 'residency' && check.method === 'id_address' && <button className="market-button secondary" disabled={p.busy} onClick={() => p.onSimulate(check.id, 'completed', { addressRegion: 'elsewhere' })}>{vt('testElsewhere')}</button>}
          <button className="market-button secondary" disabled={p.busy} onClick={() => p.onSimulate(check.id, 'failed', { reason: 'check_failed' })}>{vt('testFail')}</button>
          <button className="market-button secondary" disabled={p.busy} onClick={() => p.onSimulate(check.id, 'needs_info', { reason: 'evidence_unclear' })}>{vt('testMore')}</button>
        </div>
      )}
    </article>
  )
}
